<template>
  <div class="el-main">
    <ShokurekishoSkillsCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
    />
    <ShokurekishoCareerJobHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
    />
    <ShokurekishoJobSummaryCard
      :current-card-id="currentCardId"
      :example-sentences="exampleSentences"
    />
    <ShokurekishoPrMessageCard
      :current-card-id="currentCardId"
      :example-sentences="exampleSentences"
    />
    <ShokurekishoCreateDateCard :current-card-id="currentCardId" />

    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>作成した職務経歴書を確認</h2>
        </div>
      </div>
    </div>

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <CompletedOutputPanel
        :formatId="formatId"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        @update:isSendYagiEmailEnabled="isSendYagiEmailEnabled = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyShokurekishoFormatData,
  provideShokurekishoFormatData,
  type ShokurekishoExampleSentences
} from '@/composables/format-edit/useShokurekishoFormatData'
import {
  provideShokurekishoHistories,
  useShokurekishoHistories
} from '@/composables/format-edit/useShokurekishoHistories'
import ShokurekishoSkillsCard from '@/components/format-edit/shokurekisho/ShokurekishoSkillsCard.vue'
import ShokurekishoCareerJobHistoryCard from '@/components/format-edit/shokurekisho/ShokurekishoCareerJobHistoryCard.vue'
import ShokurekishoJobSummaryCard from '@/components/format-edit/shokurekisho/ShokurekishoJobSummaryCard.vue'
import ShokurekishoPrMessageCard from '@/components/format-edit/shokurekisho/ShokurekishoPrMessageCard.vue'
import ShokurekishoCreateDateCard from '@/components/format-edit/shokurekisho/ShokurekishoCreateDateCard.vue'
import CompletedOutputPanel from '@/components/format-edit/completed/CompletedOutputPanel.vue'
import type { BasicData } from '@/utils/format-edit/basicData'

defineProps<{
  basicData: BasicData
  formatId?: string
  currentCardId?: string
  exampleSentences?: ShokurekishoExampleSentences
}>()

const { formLib, vm, yagiprimePaymentService } = useHostServices()

const { formatData, saveKey, saveName, changeCount } = useFormatLocalData({
  saveName: 'shokumukeirekisho',
  saveOtherName: 'rirekisho',
  createEmpty: createEmptyShokurekishoFormatData
})
provideShokurekishoFormatData(formatData)

const histories = useShokurekishoHistories(formatData)
provideShokurekishoHistories(histories)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)

const getMode = computed(() => process.env.VUE_APP_MODE)

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

type PaymentLifecycle = {
  init: (host: unknown) => void
  cleanup: (host: unknown) => void
}

const paymentService =
  yagiprimePaymentService as typeof yagiprimePaymentService & PaymentLifecycle

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  if (!formatData.skillsToUse) formatData.skillsToUse = []
  if (!formatData.jobHistories) formatData.jobHistories = []
  paymentService.init(vm)
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
  paymentService.cleanup(vm)
})

defineExpose({
  saveName,
  saveKey,
  formatData,
  changeCount,
  addSkillHistory: histories.addSkillHistory,
  addJobHistory: histories.addJobHistory
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
